import { Button, Card, Form, Input, Select, Tooltip, message, Spin, Upload, Image } from 'antd';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AppLayout from '../../layouts/app.layout';
import axios from '../../config/axios';
import { Config } from '../../types/config.types';
import { handleAPIError } from '../../utils/helpers';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import MDMHealthCard from '../../components/mdm-health';

const SystemConfig = () => {
	const [form] = Form.useForm<Config>();
	const queryClient = useQueryClient();
	const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
	const [uploading, setUploading] = useState(false);

	// Gateway options
	const gatewayOptions = [
		{ label: 'Beyonic', value: 'beyonic' },
		{ label: 'Yo', value: 'yo' },
	];

	// SMS Gateway options
	const smsGatewayOptions = [
		{ label: 'Route SMS', value: 'route_sms' },
		{ label: 'Ego', value: 'ego' },
	];

	// Fetch current config
	const { data, isLoading, error } = useQuery({
		queryKey: ['config'],
		queryFn: async () => {
			const response = await axios.get('/admin/fundamentals/get-config', {
				withCredentials: true,
			});
			return response.data;
		},
	});

	// Update config mutation
	const { mutate: updateConfig, isPending } = useMutation({
		mutationFn: async (values: Config) => {
			const response = await axios.put('/admin/fundamentals/update-config', values, {
				withCredentials: true,
			});
			return response.data;
		},
		onSuccess: () => {
			message.success('Configuration settings updated successfully');
			// Refresh the config data
			queryClient.invalidateQueries({ queryKey: ['config'] });
		},
		onError: (error) => {
			handleAPIError(error);
		},
	});

	// Set form values when data is loaded
	useEffect(() => {
		if (data?.results) {
			form.setFieldsValue(data.results);
			// Set QR code URL if it exists
			if (data.results.qr_code) {
				setQrCodeUrl(data.results.qr_code);
			}
		}
	}, [data, form]);

	// Handle form submission
	const handleSubmit = (values: Config) => {
		updateConfig(values);
	};

	// Handle QR Code upload
	const handleQrCodeUpload = async (file: File) => {
		try {
			setUploading(true);
			const formData = new FormData();
			formData.append('file', file);

			const response = await axios.post('/fundamentals/file-upload', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				withCredentials: true,
			});

			if (response.data.err === false) {
				const secureUrl = response.data.results.secure_url;
				setQrCodeUrl(secureUrl);
				// Update the form field value
				form.setFieldValue('qr_code', secureUrl);
				message.success('QR Code uploaded successfully');
			}
		} catch (error) {
			handleAPIError(error as Error);
		} finally {
			setUploading(false);
		}
	};

	if (error) {
		handleAPIError(error);
	}

	return (
		<AppLayout
			title="Settings"
			subtitle="Manage system configuration"
		>
			<MDMHealthCard />
			<Card className="shadow-sm">
				{isLoading ? (
					<div className="flex h-[70vh] items-center justify-center">
						<Spin size="large" />
					</div>
				) : (
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						initialValues={data || {}}
						className="max-w-2xl"
					>
						<Form.Item
							name="gateway"
							label={
								<span>
									Payment Gateway{' '}
									<Tooltip title="Select the payment processing gateway for transactions">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[{ required: true, message: 'Please select a payment gateway' }]}
						>
							<Select
								placeholder="Select payment gateway"
								options={gatewayOptions}
							/>
						</Form.Item>

						<Form.Item
							name="sms_gateway"
							label={
								<span>
									SMS Gateway{' '}
									<Tooltip title="Select the SMS provider for notifications">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[{ required: true, message: 'Please select an SMS gateway' }]}
						>
							<Select
								placeholder="Select SMS gateway"
								options={smsGatewayOptions}
							/>
						</Form.Item>

						<Form.Item
							name="down_payment_percentage"
							label={
								<span>
									Down Payment (%){' '}
									<Tooltip title="Initial payment percentage required for purchases">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[
								{ required: true, message: 'Please enter the down payment percentage' },
								{ pattern: /^[0-9]+(\.[0-9]+)?$/, message: 'Please enter a valid number' },
							]}
						>
							<Input
								placeholder="Down payment percentage"
								suffix="%"
							/>
						</Form.Item>

						<Form.Item
							name="service_fee"
							label={
								<span>
									Service Fee (%){' '}
									<Tooltip title="Fee charged for service processing">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[
								{ required: true, message: 'Please enter the service fee' },
								{ pattern: /^[0-9]+(\.[0-9]+)?$/, message: 'Please enter a valid number' },
							]}
						>
							<Input placeholder="Service fee" />
						</Form.Item>

						<Form.Item
							name="zazu_funded_intereset_percentage"
							label={
								<span>
									Zazu Funded Interest Percentage{' '}
									<Tooltip title="Percentage of interest charged for Zazu funded purchases">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[
								{ required: true, message: 'Please enter the Zazu funded interest percentage' },
								{ pattern: /^[0-9]+(\.[0-9]+)?$/, message: 'Please enter a valid number' },
							]}
						>
							<Input placeholder="Service fee" />
						</Form.Item>

						<Form.Item
							name="management_fee_per_month"
							label={
								<span>
									Management Fee (per month){' '}
									<Tooltip title="Monthly fee charged for account management">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[
								{ required: true, message: 'Please enter the management fee per month' },
								{ pattern: /^[0-9]+(\.[0-9]+)?$/, message: 'Please enter a valid number' },
							]}
						>
							<Input placeholder="Management fee per month" />
						</Form.Item>

						<Form.Item
							name="qr_code"
							label={
								<span>
									QR Code{' '}
									<Tooltip title="Upload a QR code image for payment or other purposes">
										<InfoCircleOutlined />
									</Tooltip>
								</span>
							}
							rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
						>
							<div className="flex flex-col gap-4">
								<Input
									type="hidden"
									value={qrCodeUrl}
								/>

								<div className="flex items-center gap-4">
									<Upload
										accept="image/*"
										showUploadList={false}
										beforeUpload={(file) => {
											handleQrCodeUpload(file);
											return false; // Prevent default upload behavior
										}}
									>
										<Button
											icon={<UploadOutlined />}
											loading={uploading}
											disabled={uploading}
										>
											Upload QR Code
										</Button>
									</Upload>

									<div className="flex flex-col gap-2">
										<span className="text-sm text-gray-500">Current QR Code:</span>
										{qrCodeUrl ? (
											<Image
												src={qrCodeUrl}
												alt="QR Code"
												width={100}
												className="rounded-md border"
											/>
										) : (
											<div className="flex h-[100px] w-[100px] items-center justify-center rounded-md border bg-gray-100 text-gray-400">
												No QR code
											</div>
										)}
									</div>
								</div>
							</div>
						</Form.Item>

						<Form.Item className="mt-6">
							<Button
								type="primary"
								htmlType="submit"
								loading={isPending}
								disabled={isPending || uploading}
							>
								Save Settings
							</Button>
						</Form.Item>
					</Form>
				)}
			</Card>
		</AppLayout>
	);
};

export default SystemConfig;
